import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement, useCallback } from 'react';

import { MenuItemsPlaceholder } from '@gem/components';
import { IcDoneIcon } from '@gem/icons';
import { AddKeyPressHandler } from '@gem/keyboard-handler';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup, useClosePopupOnNavigation } from '@gem/popups';
import { customScrollStyles, FAB, TextField } from '@gem/ui-kit';

import { List } from 'APP/components/List/List';

import { KeywordFilterItem } from './KeywordFilterItem/KeywordFilterItem';
import {
  IKeywordFiltersPopupParams,
  useKeywordFiltersPopupPresenter,
} from './KeywordFiltersPopup.presenter';
import styles from './KeywordFiltersPopup.styles.m.css';

type TKeywordFiltersPopupProps = ICommonPopupsProps<IKeywordFiltersPopupParams>;

export const KeywordFiltersPopup: FC<TKeywordFiltersPopupProps> = observer((props) => {
  const { t } = useTranslation();
  const presenter = useKeywordFiltersPopupPresenter(props);
  useClosePopupOnNavigation(props.popupInstance);

  const renderItem = useCallback((item: string): ReactElement => {
    return (
      <KeywordFilterItem
        value={item}
        onDelete={(): void => presenter.onDeleteValue(item)}
      />
    );
  }, []);

  return (
    <Popup
      title={t('manage_offencive_words')}
      isOpened
      isHidden={props.popupInstance.isHidden}
      handlePrevStep={presenter.hasBackButton ? presenter.onBack : null}
      onClose={presenter.onClose}
    >
      <AddKeyPressHandler onEsc={presenter.onClose} />
      <div className={styles.content}>
        <div className={styles.header}>
          <p className={styles.description}>{t('comments_filtering_description')}</p>
          <div className={styles.addContainer}>
            <TextField
              className={styles.addInput}
              name={'keywords-add-input'}
              value={presenter.addValue}
              label={t('comments_filtering_placeholder_add')}
              message={t('comments_filtering_placeholder')}
              maxLength={100}
              autoComplete="off"
              onChange={presenter.onAddValueChange}
              onKeyDown={presenter.onAddValueKeyDown}
            />
            <FAB
              className={styles.addButton}
              Icon={IcDoneIcon}
              size="50"
              theme="21"
              isDisabled={!presenter.canAddValue}
              onClick={presenter.onAddValue}
            />
          </div>
        </div>

        <div className={styles.listContainer}>
          <div className={styles.divider} />
          <List
            className={classNames(styles.list, customScrollStyles.container)}
            isLoading={presenter.isLoading}
            data={presenter.list}
            keyExtractor={(item: string): string => item}
            renderItem={renderItem}
            SpinnerComponent={<MenuItemsPlaceholder count={5} />}
          />
        </div>
      </div>
    </Popup>
  );
});
